if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;
        
        // Event listeners para clicks en dashes
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        
        // Detección de swipe mediante scroll en el slider
        const sliderElement = this.elements.viewer.querySelector('.product__media-list');
        if (sliderElement) {
          let scrollTimeout;
          sliderElement.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              this.updateDashFromScroll();
            }, 30);
          });
        }
        
        // Marcar primer dash activo al cargar
        setTimeout(() => {
          const firstDash = this.elements.thumbnails.querySelector('[data-target] button');
          if (firstDash) {
            firstDash.setAttribute('aria-current', 'true');
          }
          
          // Reproducir video del primer slide si existe
          const firstSlide = this.elements.viewer.querySelector('[data-media-id]');
          if (firstSlide) {
            this.playActiveMedia(firstSlide);
          }
        }, 100);
        
        // Inicializar observador de viewport para videos
        this.initVideoViewportObserver();
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Nuevo método para inicializar el observador de viewport
      initVideoViewportObserver() {
        if (!window.IntersectionObserver) return;
        
        const options = {
          root: null, // viewport
          threshold: 0.1, // 10% visible para activar
          rootMargin: '0px'
        };
        
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            const video = entry.target;
            
            if (entry.isIntersecting) {
              // Video está visible, reproducir
              this.playVideoInViewport(video);
            } else {
              // Video no está visible, pausar
              this.pauseVideoOutViewport(video);
            }
          });
        }, options);
        
        // Observar todos los videos en la galería
        this.observeAllVideos();
      }
      
      // Observar todos los videos existentes
      observeAllVideos() {
        if (!this.videoObserver) return;
        
        const videos = this.elements.viewer.querySelectorAll('video');
        videos.forEach(video => {
          this.videoObserver.observe(video);
        });
      }
      
      // Reproducir video cuando entra al viewport
      playVideoInViewport(video) {
        if (video.paused) {
          // Configurar video para reproducción automática
          video.muted = true; // Necesario para autoplay
          video.play().catch(e => {
            console.log('Video autoplay prevented:', e);
          });
        }
      }
      
      // Pausar video cuando sale del viewport
      pauseVideoOutViewport(video) {
        if (!video.paused) {
          video.pause();
        }
      }

      updateDashFromScroll() {
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        const container = this.elements.viewer;
        const containerRect = container.getBoundingClientRect();
        
        let activeSlide = null;
        let maxVisibility = 0;
        
        slides.forEach(slide => {
          const slideRect = slide.getBoundingClientRect();
          const slideLeft = slideRect.left - containerRect.left;
          const slideRight = slideRect.right - containerRect.left;
          
          // Calcular qué porcentaje del slide está visible
          const visibleLeft = Math.max(0, slideLeft);
          const visibleRight = Math.min(containerRect.width, slideRight);
          const visibleWidth = Math.max(0, visibleRight - visibleLeft);
          const visibilityRatio = visibleWidth / slideRect.width;
          
          if (visibilityRatio > maxVisibility) {
            maxVisibility = visibilityRatio;
            activeSlide = slide;
          }
        });
        
        if (activeSlide) {
          const mediaId = activeSlide.dataset.mediaId;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          
          // Actualizar dash activo
          this.elements.thumbnails.querySelectorAll('button').forEach(button => {
            button.removeAttribute('aria-current');
          });
          
          if (activeThumbnail) {
            activeThumbnail.querySelector('button').setAttribute('aria-current', 'true');
          }
          
          // Reproducir video del slide activo
          this.playActiveMedia(activeSlide);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        // Pausar cualquier video que esté reproduciéndose (por compatibilidad)
        const allVideos = this.elements.viewer.querySelectorAll('video');
        allVideos.forEach(video => {
          if (!video.paused) video.pause();
        });

        // Buscar el video en el slide activo (si existe)
        const video = activeItem.querySelector('video');
        if (video) {
          video.muted = true; // Asegurar que está muteado para autoplay
          video.play().catch(e => console.log('Video autoplay prevented'));
        }

        // Funcionalidad original para otros tipos de media
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
      
      // Limpiar observador cuando se destruye el componente
      disconnectedCallback() {
        if (this.videoObserver) {
          this.videoObserver.disconnect();
          this.videoObserver = null;
        }
      }
    }
  );
}